import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import PropTypes from "prop-types";

/**
 * MetricPicker - the `@`-mention dropdown NarrativeTokenField opens while an author types
 * `@partial-query`. Lists `catalog` metrics grouped by `group`, each row showing its `label` +
 * the LIVE value from `metrics[key]` (or "—" when the metric hasn't resolved yet / is missing) -
 * the same live figures the report's chart blocks already display, so an author sees exactly what
 * the token will render before committing to it.
 *
 * Filtering is a case-insensitive substring match against EITHER `label` or `key` - an author who
 * remembers the raw catalog key (e.g. "forecast_midpoint") gets the same hit as one who types the
 * human label ("Forecast").
 *
 * Keyboard: ArrowUp/ArrowDown move a single highlighted row (wrapping at either end); Enter picks
 * the highlighted row (`onPick(key)`); Escape calls `onClose()`. The highlighted row carries
 * `aria-selected="true"` (every other row `"false"`) so assistive tech and tests can both locate
 * it without relying on visual-only styling. Clicking a row picks it directly, regardless of
 * which row was keyboard-highlighted.
 *
 * Renders nothing when `open` is falsy - NarrativeTokenField mounts this unconditionally and
 * flips `open`, rather than conditionally mounting/unmounting it, so the picker's own effects
 * (the highlight-reset below) run predictably.
 *
 * KEYBOARD FORWARDING (SP6 rework): in a real composition the caret lives in the
 * `contentEditable` field, NOT in this listbox, so this component's own `onKeyDown` never fires
 * during an `@`-mention. NarrativeTokenField therefore holds a ref to us and forwards the arrow/
 * Enter/Escape keys via the imperative `handleKey(key)` handle below - which runs the SAME
 * `runKey` logic the listbox's own `onKeyDown` uses, so highlight/wrap/pick/close stay a single
 * source of truth inside MetricPicker (the parent never has to know about `flatRows` or wrapping).
 * The listbox `onKeyDown` is retained so the component still works standalone (and its isolation
 * tests stay green).
 */
const MetricPicker = forwardRef(function MetricPicker(
  { open, query, catalog, metrics, onPick, onClose },
  ref
) {
  const groups = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    const filtered = (catalog || []).filter((metric) => {
      if (!metric) return false;
      if (!q) return true;
      const label = (metric.label || "").toLowerCase();
      const key = (metric.key || "").toLowerCase();
      return label.includes(q) || key.includes(q);
    });

    const byGroup = new Map();
    filtered.forEach((metric) => {
      const groupName = metric.group || "";
      if (!byGroup.has(groupName)) byGroup.set(groupName, []);
      byGroup.get(groupName).push(metric);
    });
    return Array.from(byGroup.entries()).map(([name, rows]) => ({ name, rows }));
  }, [catalog, query]);

  const flatRows = useMemo(() => groups.flatMap((group) => group.rows), [groups]);

  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Reset the highlight whenever the visible row set changes (query narrows/widens, or the
  // picker re-opens) - an author retyping the query shouldn't land on a highlight that no longer
  // corresponds to a visible row.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, open]);

  const moveHighlight = (delta) => {
    if (flatRows.length === 0) return;
    setHighlightedIndex((prev) => {
      const next = (prev + delta + flatRows.length) % flatRows.length;
      return next;
    });
  };

  // runKey - the shared key-handling core, driven both by the listbox's own onKeyDown (standalone
  // use) and by NarrativeTokenField via the imperative handle (composed use). Returns true when it
  // handled the key, so the caller can `preventDefault` on the real event only when appropriate.
  const runKey = (key) => {
    if (key === "ArrowDown") {
      moveHighlight(1);
      return true;
    }
    if (key === "ArrowUp") {
      moveHighlight(-1);
      return true;
    }
    if (key === "Enter") {
      const row = flatRows[highlightedIndex];
      if (row && onPick) onPick(row.key);
      return true;
    }
    if (key === "Escape") {
      if (onClose) onClose();
      return true;
    }
    return false;
  };

  // Expose runKey imperatively so the parent contentEditable field can forward the keys the caret
  // "steals" while the picker is open. Recreated every render so the closure always sees the
  // latest highlightedIndex / flatRows (no stale-closure picks). This hook runs on every render
  // (before the `if (!open) return null` below), so the imperative handle is always registered -
  // `ref.current` stays populated even while closed; callers still guard on `pickerRef.current`
  // and only forward keys while the picker is open.
  useImperativeHandle(ref, () => ({ handleKey: runKey }));

  if (!open) return null;

  const handleKeyDown = (e) => {
    if (runKey(e.key)) e.preventDefault();
  };

  return (
    <div className="metric-picker" role="presentation">
      {flatRows.length === 0 ? (
        <div className="metric-picker-empty" role="status">
          No metrics match &ldquo;{query}&rdquo;
        </div>
      ) : (
        <ul
          className="metric-picker-list"
          role="listbox"
          aria-label="Insert metric"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          {groups.map((group) => (
            <li key={group.name || "ungrouped"} className="metric-picker-group">
              <div className="metric-picker-group-label" role="presentation">
                {group.name}
              </div>
              <ul className="metric-picker-group-rows" role="presentation">
                {group.rows.map((metric) => {
                  const flatIndex = flatRows.indexOf(metric);
                  const isHighlighted = flatIndex === highlightedIndex;
                  const liveValue = metrics?.[metric.key]?.value ?? "—";
                  return (
                    <li
                      key={metric.key}
                      role="option"
                      aria-selected={isHighlighted}
                      className={"metric-picker-row" + (isHighlighted ? " is-highlighted" : "")}
                      onMouseEnter={() => setHighlightedIndex(flatIndex)}
                      onClick={() => onPick && onPick(metric.key)}
                    >
                      <span className="metric-picker-row-label">{metric.label}</span>
                      <span className="metric-picker-row-value">{liveValue}</span>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

MetricPicker.propTypes = {
  open: PropTypes.bool,
  query: PropTypes.string,
  catalog: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      group: PropTypes.string,
    })
  ),
  metrics: PropTypes.object,
  onPick: PropTypes.func,
  onClose: PropTypes.func,
};

export default MetricPicker;
